/**
 * Cookie consent — GDPR/CCPA-aware preference store.
 *
 * Persists the user's categorical consent choices in localStorage under
 * a versioned key so we can cleanly invalidate when our categories change.
 *
 * Categories:
 *   necessary  — session/auth, CSRF, rate-limit; always on (required).
 *   analytics  — product analytics (PostHog, page-view, funnel events).
 *   marketing  — future advertising/retargeting pixels (none today).
 *
 * Public API:
 *   useConsent()            → { preferences, hasDecided, openSettings, ... }
 *   acceptAll() / rejectAll()
 *   update({ analytics, marketing })
 *   openSettings() / closeSettings()
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";

const STORAGE_KEY = "qt_consent_v1";

const DEFAULT_PREFERENCES = {
  necessary: true,       // always true
  analytics: false,
  marketing: false,
};

function readStored() {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      preferences: {
        necessary: true,
        analytics: Boolean(parsed.analytics),
        marketing: Boolean(parsed.marketing),
      },
      decided_at: parsed.decided_at || null,
    };
  } catch (_) {
    return null;
  }
}

function writeStored(preferences) {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        analytics: Boolean(preferences.analytics),
        marketing: Boolean(preferences.marketing),
        decided_at: new Date().toISOString(),
      })
    );
  } catch (_) {
    // Private-mode browsers / SSR — no-op.
  }
}

const ConsentCtx = createContext(null);

export function ConsentProvider({ children }) {
  const [state, setState] = useState(() => {
    const existing = readStored();
    if (existing) {
      return {
        preferences: existing.preferences,
        hasDecided: true,
        decided_at: existing.decided_at,
      };
    }
    return { preferences: DEFAULT_PREFERENCES, hasDecided: false, decided_at: null };
  });
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Broadcast a custom event so listeners (e.g. lib/posthog.js) can
  // react to consent changes without importing this module directly.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent("qt:consent-change", {
        detail: { preferences: state.preferences, hasDecided: state.hasDecided },
      })
    );
  }, [state.preferences, state.hasDecided]);

  const acceptAll = useCallback(() => {
    const prefs = { necessary: true, analytics: true, marketing: true };
    writeStored(prefs);
    setState({ preferences: prefs, hasDecided: true, decided_at: new Date().toISOString() });
    setSettingsOpen(false);
  }, []);

  const rejectAll = useCallback(() => {
    const prefs = { necessary: true, analytics: false, marketing: false };
    writeStored(prefs);
    setState({ preferences: prefs, hasDecided: true, decided_at: new Date().toISOString() });
    setSettingsOpen(false);
  }, []);

  const update = useCallback((partial) => {
    const prefs = {
      necessary: true,
      analytics: Boolean(partial.analytics),
      marketing: Boolean(partial.marketing),
    };
    writeStored(prefs);
    setState({ preferences: prefs, hasDecided: true, decided_at: new Date().toISOString() });
    setSettingsOpen(false);
  }, []);

  const openSettings = useCallback(() => setSettingsOpen(true), []);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  const value = useMemo(
    () => ({
      preferences: state.preferences,
      hasDecided: state.hasDecided,
      decidedAt: state.decided_at,
      settingsOpen,
      acceptAll,
      rejectAll,
      update,
      openSettings,
      closeSettings,
    }),
    [state, settingsOpen, acceptAll, rejectAll, update, openSettings, closeSettings]
  );

  return <ConsentCtx.Provider value={value}>{children}</ConsentCtx.Provider>;
}

export function useConsent() {
  const ctx = useContext(ConsentCtx);
  if (!ctx) {
    // Safe default for code paths that run before the provider mounts.
    return {
      preferences: DEFAULT_PREFERENCES,
      hasDecided: false,
      decidedAt: null,
      settingsOpen: false,
      acceptAll: () => {},
      rejectAll: () => {},
      update: () => {},
      openSettings: () => {},
      closeSettings: () => {},
    };
  }
  return ctx;
}

/**
 * Low-level read for non-React code (e.g. lib/posthog.js init). Returns
 * the persisted preferences or null if the user hasn't decided yet.
 */
export function readConsentSync() {
  return readStored();
}
